import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { config } from '../config';

const backendApi = config.backendApi;


export interface AppNotification {
  id: string;
  category: string;
  title: string;
  body: string | null;
  link: string | null;
  sourceType: string | null;
  sourceId: string | null;
  /** NULL on rows written before the inbox carried severity (§8) — show no chip
   *  rather than inventing a classification. */
  severity: string | null;
  /** The routing event behind the row, which is what lets the inbox link across to
   *  the delivery log's account of why it arrived. NULL on pre-router rows. */
  eventId: string | null;
  /** Whether this row announces that something ended rather than started. The chip
   *  reads "Resolved" instead of the severity, because `severity` on a resolution is
   *  the peak of the incident that closed — not how urgent this row is. */
  isResolution: boolean;
  read: boolean;
  createdAt: string;
}

interface NotificationListResponse {
  notifications: AppNotification[];
  unreadCount: number;
}

export interface NotificationMute {
  sourceType: string;
  sourceId: string;
  createdAt: string;
}

// ---- inbox ----------------------------------------------------------------

const fetchNotifications = async (unreadOnly: boolean, limit: number): Promise<NotificationListResponse> => {
  const params = new URLSearchParams();
  if (unreadOnly) params.append('unread_only', 'true');
  params.append('limit', String(limit));
  const response = await axios.get<NotificationListResponse>(`${backendApi}/api/notifications?${params.toString()}`, {
    withCredentials: true,
  });
  return response.data;
};

/**
 * One page of the inbox, filtered.
 *
 * Separate from `useNotifications` on purpose: the bell wants a small, frequently
 * polled slice and the page wants a large, filtered, growing one, and sharing a query
 * key would make each refetch the other's shape. (Same-key-same-shape is a rule here.)
 */
export function useInboxPage({
  unreadOnly,
  category,
  limit,
}: {
  unreadOnly: boolean;
  category: string | null;
  limit: number;
}) {
  return useQuery<NotificationListResponse>({
    queryKey: ['inbox-page', unreadOnly, category, limit],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (unreadOnly) params.append('unread_only', 'true');
      if (category) params.append('category', category);
      params.append('limit', String(limit));
      const response = await axios.get<NotificationListResponse>(
        `${backendApi}/api/notifications?${params.toString()}`,
        { withCredentials: true },
      );
      return response.data;
    },
    // Keeps the visible rows on screen while "load more" fetches the wider page,
    // instead of blanking the list and jumping the scroll position.
    placeholderData: (previous) => previous,
  });
}

/** Inbox list + unread count. Polls so the bell badge stays roughly live. */
export function useNotifications(unreadOnly = false, limit = 50) {
  return useQuery<NotificationListResponse>({
    queryKey: ['notifications', { unreadOnly, limit }],
    queryFn: () => fetchNotifications(unreadOnly, limit),
    refetchInterval: 60_000,
  });
}

const fetchUnreadCount = async (): Promise<number> => {
  const response = await axios.get<{ count: number }>(`${backendApi}/api/notifications/unread-count`, {
    withCredentials: true,
  });
  return response.data.count;
};

/** Lightweight unread-count poll for the bell badge. */
export function useUnreadCount() {
  return useQuery<number>({
    queryKey: ['notifications-unread-count'],
    queryFn: fetchUnreadCount,
    refetchInterval: 60_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${backendApi}/api/notifications/${id}/read`, {}, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      // The inbox page keeps its own key (different shape, different cadence), so it
      // does not ride along on the bell's invalidation and has to be named.
      void queryClient.invalidateQueries({ queryKey: ['inbox-page'] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => axios.post(`${backendApi}/api/notifications/read-all`, {}, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
      // The inbox page keeps its own key (different shape, different cadence), so it
      // does not ride along on the bell's invalidation and has to be named.
      void queryClient.invalidateQueries({ queryKey: ['inbox-page'] });
    },
  });
}

// ---- mutes ----------------------------------------------------------------

/**
 * The `source_type` for muting one incident. The backend validates against its own
 * closed set (`cm` | `incident`) and 400s on anything else, because a typo'd source
 * type stores a row that matches nothing: the UI would show "muted" while the
 * notifications kept arriving.
 */
export const MUTE_SOURCE_INCIDENT = 'incident';

export function useNotificationMutes() {
  return useQuery<NotificationMute[]>({
    queryKey: ['notification-mutes'],
    queryFn: async () => {
      const response = await axios.get<NotificationMute[]>(`${backendApi}/api/notifications/mutes`, {
        withCredentials: true,
      });
      return response.data;
    },
  });
}

export function useMuteSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { sourceType: string; sourceId: string }) =>
      axios.post(`${backendApi}/api/notifications/mutes`, args, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-mutes'] }),
  });
}

export function useUnmuteSource() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { sourceType: string; sourceId: string }) =>
      axios.delete(`${backendApi}/api/notifications/mutes/${args.sourceType}/${args.sourceId}`, {
        withCredentials: true,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-mutes'] }),
  });
}

// ---- delivery destinations (Phase 5) --------------------------------------

export type DestinationKind = 'email' | 'web_push' | 'webhook' | 'slack_webhook' | 'teams_webhook' | 'psa';

export interface NotificationDestination {
  id: string;
  kind: DestinationKind;
  displayName: string;
  /** Non-secret rendering: a full email address, or a webhook host with its path hidden. */
  target: string;
  divisionId: string | null;
  isOrgOwned: boolean;
  verified: boolean;
  /** 'token' | 'identity_email' | 'billing_contact' | 'push_subscription' | 'migration' */
  verifiedVia: string | null;
  failureCount: number;
  lastSuccessAt: string | null;
  createdAt: string;
}

/**
 * Every destination this account owns — email addresses and enrolled browsers alike.
 *
 * Its own query key rather than sharing `['push-devices']`, which reads the same
 * endpoint but returns a narrowed, push-only shape. Two keys over one endpoint is
 * cheap; one key returning two shapes is the bug that convention exists to prevent.
 */
export function useNotificationDestinations() {
  return useQuery({
    queryKey: ['notification-destinations'],
    queryFn: async () => {
      const response = await axios.get<{ destinations: NotificationDestination[] }>(
        `${backendApi}/api/notifications/destinations`,
        { withCredentials: true },
      );
      return response.data.destinations;
    },
  });
}

/**
 * Both keys read the one endpoint, so both go stale together — and so do the rules,
 * which carry a `destinationId` into a list the composer resolves against. A rule
 * whose destination was deleted otherwise keeps the dead id in cache: the picker
 * renders blank because nothing matches, and Save resubmits the id of a row that no
 * longer exists.
 */
function invalidateDestinations(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: ['notification-destinations'] });
  void queryClient.invalidateQueries({ queryKey: ['push-devices'] });
  void queryClient.invalidateQueries({ queryKey: ['notification-rules'] });
}

/**
 * Add an email destination. Personal destinations are email-only by design — a
 * personal Slack webhook would be a shared channel one user could point at without
 * anyone else knowing — so webhook kinds live on the organization surface.
 *
 * The row is created unverified and a confirmation email goes out; nothing is
 * delivered here until that link is followed.
 */
export function useCreateEmailDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (address: string) => {
      const res = await axios.post<NotificationDestination>(
        `${backendApi}/api/notifications/destinations`,
        { kind: 'email', address },
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => { invalidateDestinations(queryClient); },
  });
}

export function useDeleteDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/notifications/destinations/${id}`, { withCredentials: true }),
    onSuccess: () => { invalidateDestinations(queryClient); },
  });
}

export interface TestDeliveryResult {
  delivered: boolean;
  detail: string | null;
  verified: boolean;
}

/**
 * Send a probe to one destination, whatever kind it is.
 *
 * Lives here rather than with the push hooks because the endpoint is
 * destination-generic and the email rows need it too — and because importing the push
 * module from the destinations list would pull the whole push feature into the trial
 * bundle, which `check-trial-bundle.mjs` fails on.
 *
 * A refusal comes back as `delivered: false` with a reason, not as an error status:
 * "not verified yet" is a state the user can act on. Only the 429 cooldown is a real
 * error response.
 */
export function useTestDestination() {
  const queryClient = useQueryClient();
  return useMutation<TestDeliveryResult, unknown, string>({
    mutationFn: async (id: string) => {
      const res = await axios.post<TestDeliveryResult>(
        `${backendApi}/api/notifications/destinations/${id}/test`,
        {},
        { withCredentials: true },
      );
      return res.data;
    },
    // A send clears the failure streak server-side, so the row's "the last N
    // deliveries here failed" line is stale the moment a test succeeds.
    onSuccess: () => { invalidateDestinations(queryClient); },
  });
}

/**
 * Re-issue the confirmation link for an unverified email destination.
 *
 * The token travels by email and only by email — the API never returns it — so without
 * this the only escape from an unverified destination is to delete and add it again.
 * A new token is minted server-side, which makes the newest link the only one that
 * works.
 */
export function useResendDestinationVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${backendApi}/api/notifications/destinations/${id}/resend`, {}, { withCredentials: true }),
    onSuccess: () => { invalidateDestinations(queryClient); },
  });
}

/** Which surface a confirmation link belongs to; the link says so explicitly. */
export type DestinationScope = 'personal' | 'org';

/**
 * Complete the emailed ceremony. All three parts come from the link: the token proves
 * control of the address, the id says which row it proves it for, and the scope says
 * which endpoint can accept the proof.
 *
 * The scope is not a nicety. Personal and organization destinations are verified
 * through different endpoints with different authorization — owner-only versus
 * `notification_route:manage` — and an id alone cannot say which a row is. Posting an
 * organization link to the personal endpoint 404s on its ownership check, which is
 * precisely how shared addresses were left permanently unconfirmable.
 */
export function useVerifyDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; token: string; scope: DestinationScope }) =>
      axios.post(
        args.scope === 'org'
          ? `${backendApi}/api/notification-destinations/${args.id}/verify`
          : `${backendApi}/api/notifications/destinations/${args.id}/verify`,
        { token: args.token },
        { withCredentials: true },
      ),
    onSuccess: () => {
      invalidateDestinations(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['org-notification-destinations'] });
    },
  });
}

/** Re-issue a shared destination's confirmation link. */
export function useResendOrgDestinationVerification() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${backendApi}/api/notification-destinations/${id}/resend`, {}, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['org-notification-destinations'] }),
  });
}

// ---- organization destinations + routes (§3.2, §5.2) ----------------------

/**
 * The organization's **shared** destinations.
 *
 * A separate endpoint and a separate key from the personal list, because they are
 * separate sets: `/api/notifications/destinations` returns rows you own,
 * `/api/notification-destinations` returns rows the organization owns and you are
 * permitted to see. A destination appears in exactly one of them.
 *
 * `enabled` so a caller without `notification_route:read` never fires a request that
 * can only 403.
 */
export function useOrgDestinations(enabled = true) {
  return useQuery({
    queryKey: ['org-notification-destinations'],
    enabled,
    queryFn: async () => {
      const response = await axios.get<{ destinations: NotificationDestination[] }>(
        `${backendApi}/api/notification-destinations/`,
        { withCredentials: true },
      );
      return response.data.destinations;
    },
  });
}

export interface OrgDestinationInput {
  kind: DestinationKind;
  displayName?: string;
  /** For `kind = email`. */
  address?: string;
  /** For the webhook-shaped kinds. */
  url?: string;
  /** Which division owns it. NULL = the whole organization. */
  divisionId?: string | null;
}

export function useCreateOrgDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: OrgDestinationInput) => {
      const res = await axios.post<NotificationDestination>(`${backendApi}/api/notification-destinations/`, input, {
        withCredentials: true,
      });
      return res.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['org-notification-destinations'] }),
  });
}

export function useDeleteOrgDestination() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/notification-destinations/${id}`, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['org-notification-destinations'] });
      // A deleted destination nulls itself out of any route that named it.
      void queryClient.invalidateQueries({ queryKey: ['notification-routes'] });
    },
  });
}

/**
 * Probe a shared destination.
 *
 * Distinct from `useTestDestination` because the org endpoint is a different path
 * with a different permission — and because for a webhook this call is not merely a
 * test: a 2xx through the SSRF guard **is** the verification (§5.2), so the routes
 * list can go from unusable to usable on its result.
 */
export function useTestOrgDestination() {
  const queryClient = useQueryClient();
  return useMutation<TestDeliveryResult, unknown, string>({
    mutationFn: async (id: string) => {
      const res = await axios.post<TestDeliveryResult>(
        `${backendApi}/api/notification-destinations/${id}/test`,
        {},
        { withCredentials: true },
      );
      return res.data;
    },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['org-notification-destinations'] }),
  });
}

export interface NotificationRoute {
  id: string;
  name: string;
  /** Ownership — which division administers this route. NULL = the organization. */
  divisionId: string | null;
  destinationId: string | null;
  /** The destination's display name — never its address (§5.2: the address is the secret). */
  destinationName: string | null;
  destinationKind: string | null;
  categories: string[] | null;
  categoriesNegated: boolean;
  minSeverity: RuleSeverity;
  maxSeverity: RuleSeverity | null;
  includeResolutions: boolean;
  alertDefinitionId: string | null;
  enabled: boolean;
  clauses: RuleClause[];
  createdAt: string;
  updatedAt: string;
}

export interface RouteInput {
  name: string;
  divisionId: string | null;
  destinationId: string;
  categories: string[] | null;
  categoriesNegated: boolean;
  minSeverity: RuleSeverity;
  maxSeverity: RuleSeverity | null;
  includeResolutions: boolean;
  enabled: boolean;
  clauses: RuleClause[];
}

/** One scope row on a simulated event. A *set* — several rows is how you simulate a
 *  multi-region incident, which is the only way to exercise the any/all quantifiers. */
export interface SimulateScope {
  divisionId?: string | null;
  regionId?: string | null;
  deviceId?: string | null;
}

export interface SimulateRequest {
  category: string;
  severity: RuleSeverity;
  isResolution: boolean;
  scopes: SimulateScope[];
}

export interface SimulatedMatch {
  /** `rule` | `route` | `forced` */
  kind: string;
  id: string | null;
  userId: string | null;
  routeName: string | null;
  channel: string;
  destinationKind: string | null;
  /** Display name only — the simulator never returns an address. */
  destinationName: string | null;
  wouldDeliver: boolean;
  /** The delivery status the router would record, when it would not deliver. */
  suppressedBy: string | null;
  detail: string | null;
}

export interface SimulateResult {
  matches: SimulatedMatch[];
  rulesConsidered: number;
  rulesMatched: number;
  routesConsidered: number;
  routesMatched: number;
  totalUsers: number;
  totalDestinations: number;
  /** True when the caller lacks `notification_route:read` and is seeing only their own
   *  rows. Without surfacing this, the answer is indistinguishable from "nothing else
   *  matched" — a materially different statement. */
  filteredToSelf: boolean;
}

/**
 * "Who would this notify?" — the real planner run against an event that is never
 * written. A mutation rather than a query because it is an explicit ask with a body,
 * not a cacheable read of existing state.
 */
export function useSimulateNotification() {
  return useMutation<SimulateResult, unknown, SimulateRequest>({
    mutationFn: async (event) => {
      const res = await axios.post<SimulateResult>(
        `${backendApi}/api/notifications/simulate`,
        { event },
        { withCredentials: true },
      );
      return res.data;
    },
  });
}

export function useNotificationRoutes(enabled = true) {
  return useQuery({
    queryKey: ['notification-routes'],
    enabled,
    queryFn: async () => {
      const response = await axios.get<{ routes: NotificationRoute[] }>(`${backendApi}/api/notification-routes/`, {
        withCredentials: true,
      });
      return response.data.routes;
    },
  });
}

export function useCreateNotificationRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (route: RouteInput) =>
      axios.post(`${backendApi}/api/notification-routes/`, route, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-routes'] }),
  });
}

export function useUpdateNotificationRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; route: RouteInput }) =>
      axios.put(`${backendApi}/api/notification-routes/${args.id}`, args.route, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-routes'] }),
  });
}

export function useDeleteNotificationRoute() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => axios.delete(`${backendApi}/api/notification-routes/${id}`, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-routes'] }),
  });
}

// ---- actions-migration report (§4) ---------------------------------------

export interface MigrationReport {
  id: string;
  alertDefinitionId: string | null;
  alertDefinitionName: string;
  actionType: string;
  /** `migrated` | `needs_manual_setup` | `dropped_never_delivered` */
  outcome: string;
  routeId: string | null;
  destinationId: string | null;
  caveats: string[];
  /** The stripped action, with credential-bearing parts already redacted server-side. */
  original: unknown;
  createdAt: string;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
}

export interface MigrationReportList {
  reports: MigrationReport[];
  unacknowledged: number;
}

/**
 * What the actions migration did to this organization's paging.
 *
 * §4 strips channel actions off `alert_definitions` and re-expresses them as routes
 * — the one moment in the design where somebody could stop being paged and never find
 * out. This is the evidence they did not.
 *
 * `enabled` because the endpoint is gated on `notification_route:read`; firing it for
 * a caller without that permission is a 403 on every page load.
 */
export function useMigrationReports(enabled = true) {
  return useQuery<MigrationReportList>({
    queryKey: ['notification-migration-reports'],
    enabled,
    queryFn: async () => {
      const response = await axios.get<MigrationReportList>(
        `${backendApi}/api/notification-migration-reports/`,
        { withCredentials: true },
      );
      return response.data;
    },
  });
}

/** Acknowledging is a claim that someone has dealt with the caveats, so it is per row. */
export function useAcknowledgeMigrationReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.post(`${backendApi}/api/notification-migration-reports/${id}/acknowledge`, {}, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-migration-reports'] }),
  });
}

// ---- routing rules (Phase 1) ----------------------------------------------

/**
 * The router's category registry (§2). Distinct from `NOTIFICATION_CATEGORIES`
 * above, which is the retiring CM preferences vocabulary — note `change_problems`
 * here versus `problems` there. The two converge in Phase 5, when CM becomes a
 * router producer and its stored preferences are translated into rules.
 */
export const RULE_CATEGORIES: { key: string; label: string; description: string }[] = [
  { key: 'incidents', label: 'Incidents', description: 'Something is wrong on the network.' },
  { key: 'approvals', label: 'Approvals', description: 'A change is waiting on you.' },
  { key: 'assigned', label: 'Assigned to me', description: 'A step is yours, or blocked.' },
  { key: 'change_problems', label: 'Change problems', description: 'A step or plan failed during a change.' },
  { key: 'activity', label: 'Activity', description: 'State changes on things you participate in.' },
  { key: 'comments', label: 'Comments', description: 'A comment or mention.' },
  { key: 'system', label: 'System', description: 'Collector, gateway, billing and account events.' },
  { key: 'reports', label: 'Reports', description: 'Reports you submitted or subscribed to.' },
];

/** Least severe first, so `SEVERITIES.indexOf(a) >= SEVERITIES.indexOf(b)` reads as "at least". */
export const SEVERITIES = ['info', 'low', 'medium', 'high', 'critical'] as const;
export type RuleSeverity = (typeof SEVERITIES)[number];

export type RuleChannel = 'in_app' | 'email' | 'push';

/**
 * One clause: a possibly-negated, independently-quantified predicate over the
 * event's scope set (§3.1.1).
 *
 * `quantifier` is the "affecting ⇄ confined to" toggle and `negated` is "is ⇄ is
 * not". The four combinations are all meaningful — "not confined to the lab" stays
 * quiet about lab-only incidents but pages you when one spreads, which is not
 * expressible any other way.
 */
/** Which object a tag clause asks about (§3.1.1). Absent on division/region clauses. */
export type TagScope = 'scope' | 'alert_definition';

export interface RuleClause {
  /**
   * The disjunction this clause belongs to: clauses sharing a `groupId` **OR**
   * together, groups **AND**. Absent means "its own group", which is every clause
   * written before groups existed.
   *
   * On write the server treats it as a grouping *token* and mints its own ids, so any
   * stable string works — the editor uses `crypto.randomUUID()`.
   */
  groupId?: string | null;
  divisionId?: string | null;
  regionId?: string | null;
  tagDefinitionId?: string | null;
  /** `null` means **any value** of the definition — a real selection, not "unset". */
  tagValueId?: string | null;
  /**
   * `scope` reads tags on the devices and regions the event touched; `alert_definition`
   * reads tags on the definition that raised it. The same tag means different things
   * on each axis, so this is not a detail the UI may default away.
   */
  tagScope?: TagScope | null;
  quantifier: 'any' | 'all';
  negated: boolean;
}

export interface NotificationRule {
  id: string;
  organizationId: string;
  categories: string[] | null;
  categoriesNegated: boolean;
  minSeverity: RuleSeverity;
  maxSeverity: RuleSeverity | null;
  includeResolutions: boolean;
  channel: RuleChannel;
  destinationId: string | null;
  delivery: RuleDelivery;
  enabled: boolean;
  clauses: RuleClause[];
  seeded: boolean;
  createdAt: string;
}

/// How often a rule delivers (§7.6). Digests are email-only (Q10): a digest push
/// carries the interruption cost of a page with the information content of a badge.
export type RuleDelivery = 'immediate' | 'hourly_digest' | 'daily_digest' | 'weekly_digest';

export const RULE_DELIVERY_LABELS: Record<RuleDelivery, string> = {
  immediate: 'Immediately',
  hourly_digest: 'Hourly digest',
  daily_digest: 'Daily digest',
  weekly_digest: 'Weekly digest',
};

export interface RuleInput {
  categories: string[] | null;
  categoriesNegated: boolean;
  minSeverity: RuleSeverity;
  maxSeverity: RuleSeverity | null;
  includeResolutions: boolean;
  channel: RuleChannel;
  delivery: RuleDelivery;
  destinationId: string | null;
  enabled: boolean;
  clauses: RuleClause[];
}

interface RuleListResponse {
  rules: NotificationRule[];
  emailAvailable: boolean;
  /** SaaS edition with a VAPID key provisioned. Describes the deployment, not the plan. */
  pushAvailable: boolean;
}

export function useNotificationRules() {
  return useQuery<RuleListResponse>({
    queryKey: ['notification-rules'],
    queryFn: async () => {
      const response = await axios.get<RuleListResponse>(`${backendApi}/api/notifications/rules`, {
        withCredentials: true,
      });
      return response.data;
    },
  });
}

export function useCreateNotificationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (rule: RuleInput) =>
      axios.post<NotificationRule>(`${backendApi}/api/notifications/rules`, rule, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

export function useUpdateNotificationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; rule: RuleInput }) =>
      axios.put<NotificationRule>(`${backendApi}/api/notifications/rules/${args.id}`, args.rule, {
        withCredentials: true,
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

export function useDeleteNotificationRule() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      axios.delete(`${backendApi}/api/notifications/rules/${id}`, { withCredentials: true }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['notification-rules'] }),
  });
}

// ---- delivery log ----------------------------------------------------------

export interface NotificationDelivery {
  id: string;
  eventId: string;
  channel: string;
  status: string;
  /** The router's own words for `status` — rendered server-side so the log and the
   *  explain drawer cannot drift apart. */
  statusLabel: string;
  detail: string | null;
  attemptCount: number;
  destinationLabel: string | null;
  isMine: boolean;
  /** What it was about. Present only where the row stands alone — see the standalone
   *  delivery log; null wherever the response already nests rows under their event. */
  eventTitle: string | null;
  eventCategory: string | null;
  eventSeverity: string | null;
  eventLink: string | null;
  createdAt: string;
}

/**
 * Everything the router decided about you, newest first.
 *
 * The unfiltered form of the endpoint is deliberately *your* history rather than the
 * organization's — the question it answers is "why didn't I hear about that?", and
 * that is a question about your own rules and addresses.
 *
 * A rule that simply did not match writes no row: the log would otherwise grow with
 * every rule anyone ever wrote. Non-matches live in the explain drawer, which is the
 * expensive on-demand half of the same split.
 */
/**
 * The caller's delivery log, or one event's.
 *
 * `eventId` switches the endpoint from "rows addressed to me" to "every row for this
 * event" — a different authorization path server-side, which authorizes the event
 * first and so may legitimately 404. That is what the inbox's "Delivery details" link
 * lands on.
 */
export function useMyDeliveries(limit = 50, eventId?: string | null) {
  return useQuery<NotificationDelivery[]>({
    queryKey: ['my-deliveries', limit, eventId ?? null],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('limit', String(limit));
      if (eventId) params.append('eventId', eventId);
      const response = await axios.get<{ deliveries: NotificationDelivery[] }>(
        `${backendApi}/api/notifications/deliveries?${params.toString()}`,
        { withCredentials: true },
      );
      return response.data.deliveries;
    },
  });
}

export interface IncidentNotificationEvent {
  eventId: string;
  eventType: string;
  severity: string;
  createdAt: string;
  deliveries: NotificationDelivery[];
}

/**
 * Every routed event for one incident, with its deliveries. The cheap load: one
 * indexed read, no matcher. Asking *why a rule did not match* is a separate,
 * expensive call — the same split the suppression endpoints established.
 */
export function useIncidentNotifications(incidentId: string | undefined) {
  return useQuery<IncidentNotificationEvent[]>({
    queryKey: ['incident-notifications', incidentId ?? null],
    enabled: !!incidentId,
    queryFn: async () => {
      const response = await axios.get<{ events: IncidentNotificationEvent[] }>(
        `${backendApi}/api/incidents/${incidentId ?? ''}/notifications`,
        { withCredentials: true },
      );
      return response.data.events;
    },
  });
}

export interface RuleVerdict {
  ruleId: string;
  channel: string;
  matched: boolean;
  reason: string;
}

export interface DeliveryExplanation {
  eventId: string;
  eventType: string;
  category: string;
  severity: string;
  isResolution: boolean;
  verdicts: RuleVerdict[];
  deliveries: NotificationDelivery[];
}

/**
 * On-demand "why did (or didn't) I hear about this?". Not a query hook: opening an
 * incident must not pay for an answer only wanted when someone reaches for it.
 */
export function useDeliveryExplainFetcher() {
  return async (eventId: string): Promise<DeliveryExplanation> => {
    const response = await axios.get<DeliveryExplanation>(
      `${backendApi}/api/notifications/deliveries/explain?eventId=${eventId}`,
      { withCredentials: true },
    );
    return response.data;
  };
}

/** The rule a notification email's "turn off this rule" link names. */
export interface RuleOffLink {
  /** The rule as the rules page writes it. */
  sentence: string;
  enabled: boolean;
}

/**
 * Read the rule a turn-off link names, without turning it off. No session: the
 * signed link is the credential. Not retried — a link that is refused is refused
 * for a reason the page shows.
 */
export function useRuleOffLink(token: string) {
  return useQuery({
    queryKey: ['rule-off-link', token],
    queryFn: async () =>
      (await axios.get<RuleOffLink>(`${backendApi}/api/notifications/rule-off`, { params: { token } })).data,
    enabled: !!token,
    retry: false,
  });
}

/** Turn off the rule a turn-off link names. Off already is the same answer. */
export function useTurnOffRuleFromLink() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (token: string) =>
      (await axios.post<RuleOffLink>(`${backendApi}/api/notifications/rule-off`, null, { params: { token } })).data,
    onSuccess: (data, token) => {
      queryClient.setQueryData(['rule-off-link', token], data);
      void queryClient.invalidateQueries({ queryKey: ['notification-rules'] });
    },
  });
}
